import React, { useState } from 'react';
import { ComplianceObligationInstance, InstanceLifecycleStatus } from './types';
import { TablePagination, useTablePagination } from '../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../shared/useTableSort';

interface Props {
  instances: ComplianceObligationInstance[];
  onSelectInstance: (instance: ComplianceObligationInstance) => void;
  onCreateInstance: (data: Partial<ComplianceObligationInstance>) => void;
}

export const ObligationInstanceTable: React.FC<Props> = ({
  instances,
  onSelectInstance,
  onCreateInstance,
}) => {
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    obligation_id: 'OBL-002',
    instance_code: '',
    period_label: '',
    due_date: '',
    assigned_executor_id: 'USR-DISPATCH',
    assigned_executor_name: '',
  });

  const filtered = instances.filter((inst) => {
    if (filterStatus !== 'ALL' && inst.lifecycle_status !== filterStatus) return false;
    return true;
  });

  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(filtered);

  const instPage = useTablePagination(sortedItems, 25);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.instance_code || !formData.period_label || !formData.due_date || !formData.assigned_executor_name) return;
    onCreateInstance(formData);
    setShowModal(false);
    setFormData({
      obligation_id: 'OBL-002',
      instance_code: '',
      period_label: '',
      due_date: '',
      assigned_executor_id: 'USR-DISPATCH',
      assigned_executor_name: '',
    });
  };

  const getStatusBadge = (status: InstanceLifecycleStatus) => {
    switch (status) {
      case 'VERIFIED':
        return <span className="badge-pill badge-success">Verified</span>;
      case 'PENDING_VERIFICATION':
        return <span className="badge-pill badge-warning">Pending Verification</span>;
      case 'IN_PROGRESS':
        return <span className="badge-pill badge-info">In Progress</span>;
      case 'OPEN':
        return <span className="badge-pill badge-info">Open</span>;
      case 'WAIVED':
        return <span className="badge-pill badge-neutral">Waived</span>;
      default:
        return <span className="badge-pill badge-neutral">{status}</span>;
    }
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', gap: '12px' }}>
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: '0 0 2px 0' }}>Obligation Execution Instances</h3>
          <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
            Periodic task cycles, fulfillment evidence, independent verification, and waivers
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 10px', background: '#ffffff' }}
          >
            <option value="ALL">All Instance Statuses</option>
            <option value="OPEN">Open</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="PENDING_VERIFICATION">Pending Verification</option>
            <option value="VERIFIED">Verified (Completed)</option>
            <option value="WAIVED">Waived</option>
          </select>

          <button
            onClick={() => setShowModal(true)}
            className="btn-primary"
            style={{ width: 'auto', padding: '8px 16px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <span>+ New Execution Instance</span>
          </button>
        </div>
      </div>

      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="instance_code" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Instance Code & Period
              </SortableTh>
              <SortableTh field="obligation_title" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Parent Obligation
              </SortableTh>
              <SortableTh field="assigned_executor_name" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Executor / Dept
              </SortableTh>
              <SortableTh field="due_date" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Due Date
              </SortableTh>
              <SortableTh field="lifecycle_status" sortField={sortField} sortDirection={sortDirection} onSort={requestSort} align="center">
                Lifecycle Status
              </SortableTh>
              <SortableTh field="verification_status" sortField={sortField} sortDirection={sortDirection} onSort={requestSort} align="center">
                Verification
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                  No execution instances found.
                </td>
              </tr>
            ) : (
              instPage.paginatedItems.map((inst) => {
                const isOverdue =
                  new Date(inst.due_date) < new Date() &&
                  !['VERIFIED', 'FULFILLED', 'WAIVED', 'CANCELLED'].includes(inst.lifecycle_status);

                return (
                  <tr key={inst.id} style={{ cursor: 'pointer' }} onClick={() => onSelectInstance(inst)}>
                    <td>
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>{inst.period_label}</div>
                      <div style={{ fontSize: '11px', color: '#0284c7', fontFamily: 'monospace' }}>{inst.instance_code}</div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500, color: '#1e293b' }}>{inst.obligation_title}</div>
                      <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>{inst.obligation_code}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b' }}>{inst.assigned_executor_name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{inst.lead_department}</div>
                    </td>
                    <td style={{ fontSize: '12px' }}>
                      <div style={{ color: isOverdue ? '#dc2626' : '#475569', fontWeight: isOverdue ? 700 : 400 }}>
                        {inst.due_date.slice(0, 10)}
                      </div>
                      {isOverdue && <span className="badge-pill badge-danger" style={{ fontSize: '10px', marginTop: '2px' }}>OVERDUE</span>}
                    </td>
                    <td style={{ textAlign: 'center' }}>{getStatusBadge(inst.lifecycle_status)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span
                        className={`badge-pill ${
                          inst.verification_status === 'PASSED'
                            ? 'badge-success'
                            : 'badge-neutral'
                        }`}
                      >
                        {inst.verification_status}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectInstance(inst);
                        }}
                        className="btn-secondary"
                        style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
                      >
                        Execute & Verify →
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        currentPage={instPage.currentPage}
        totalPages={instPage.totalPages}
        pageSize={instPage.pageSize}
        totalItems={instPage.totalItems}
        onPageChange={instPage.setPage}
        onPageSizeChange={instPage.setPageSize}
        itemLabel="execution instances"
      />

      {/* Modal */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>Start New Execution Instance</h3>
              <button onClick={() => setShowModal(false)} className="close-btn">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div className="form-field">
                  <label>Instance Code</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. OBL-INST-2026-04"
                    value={formData.instance_code}
                    onChange={(e) => setFormData({ ...formData, instance_code: e.target.value })}
                  />
                </div>

                <div className="form-field">
                  <label>Period Label</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. 2026-04 (April Cycle)"
                    value={formData.period_label}
                    onChange={(e) => setFormData({ ...formData, period_label: e.target.value })}
                  />
                </div>

                <div className="form-field">
                  <label>Due Date</label>
                  <input
                    type="date"
                    required
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  />
                </div>

                <div className="form-field">
                  <label>Assigned Executor Name</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Kemal Sen (Senior Dispatcher)"
                    value={formData.assigned_executor_name}
                    onChange={(e) => setFormData({ ...formData, assigned_executor_name: e.target.value })}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  Launch Cycle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
